import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Grid, List } from "lucide-react";
import { ProductFilters } from "@/components/ProductFilters";
import { supabase } from "@/lib/supabase";

export function ProductSelector({
  onSelect,
  value,
  onApplyFilters,
  disableDefaultTemporada = false,
  showSeasonFilter = true,
  forcedTemporada = null,
}) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("all"); // all, code, description, tags, temporada
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displayMode, setDisplayMode] = useState("list"); // 'list' o 'catalog'
  const [filters, setFilters] = useState({
    temporada: null,
    tagIds: null,
    active: true // Solo productos activos por defecto en el selector
  });
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchProductsData();
  }, [filters, forcedTemporada]);

  useEffect(() => {
    if (disableDefaultTemporada || forcedTemporada) return;

    const applyDefaultTemporada = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('default_temporada_products')
          .not('title', 'is', null)
          .limit(1);

        if (error) throw error;

        const configuredTemporada = data?.[0]?.default_temporada_products?.trim?.() || data?.[0]?.default_temporada_products || null;
        if (!configuredTemporada) return;

        const { count, error: seasonError } = await supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('active', true)
          .eq('temporada', configuredTemporada)
          .limit(1);

        if (seasonError) throw seasonError;

        if ((count || 0) > 0) {
          setFilters(prev => (prev.temporada ? prev : { ...prev, temporada: configuredTemporada }));
        }
      } catch (error) {
        console.error('Error applying default temporada in ProductSelector:', error);
      }
    };

    applyDefaultTemporada();
  }, [disableDefaultTemporada, forcedTemporada]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // Si los filtros están abiertos, no cerrar automáticamente
        if (!filtersCollapsed) return;
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [filtersCollapsed]);

  const fetchProductsData = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('products')
        .select(`
          *,
          tags:product_tags(
            tag:tags(*)
          )
        `);

      // Aplicar filtro de estado activo (siempre true para el selector)
      query = query.eq('active', true);

      // Aplicar filtro de temporada
      const effectiveTemporada = forcedTemporada || filters.temporada;
      if (effectiveTemporada) {
        query = query.eq('temporada', effectiveTemporada);
      }

      // Si hay filtros de etiquetas, necesitamos hacer una consulta más compleja
      if (filters.tagIds && filters.tagIds.length > 0) {
        // Primero obtenemos los IDs de productos que tienen las etiquetas seleccionadas
        const { data: productTagsData, error: tagsError } = await supabase
          .from('product_tags')
          .select('product_id')
          .in('tag_id', filters.tagIds);

        if (tagsError) throw tagsError;

        if (productTagsData && productTagsData.length > 0) {
          const productIds = productTagsData.map(pt => pt.product_id);
          query = query.in('id', productIds);
        } else {
          // Si no hay productos con esas etiquetas, devolver array vacío
          setProducts([]);
          setFilteredProducts([]);
          setLoading(false);
          return;
        }
      }

      const { data, error } = await query.order('code', { ascending: true });

      if (error) throw error;

      const productsWithTags = data.map(product => ({
        ...product,
        tagNames: product.tags.map(t => t.tag.name).join(', ')
      }));

      setProducts(productsWithTags);
      setFilteredProducts(productsWithTags);
      // Emitir automáticamente IDs filtrados al padre (para Inventario)
      try {
        if (typeof onApplyFilters === 'function') {
          const ids = (productsWithTags || []).map(p => p.id);
          onApplyFilters({ productIds: ids, filters: { ...filters, temporada: effectiveTemporada || null } });
        }
      } catch (_) {}
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    console.log('ProductSelector - Filtros cambiados:', newFilters);
    setFilters(newFilters);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!isOpen) setIsOpen(true);
    
    if (!term) {
      setFilteredProducts(products);
      return;
    }

    const searchTermLower = term.toLowerCase();
    let filtered;

    switch (searchType) {
      case 'code':
        filtered = products.filter(p => p.code.toLowerCase().includes(searchTermLower));
        break;
      case 'description':
        filtered = products.filter(p => p.description.toLowerCase().includes(searchTermLower));
        break;
      case 'tags':
        filtered = products.filter(p => p.tagNames.toLowerCase().includes(searchTermLower));
        break;
      case 'temporada':
        filtered = products.filter(p => p.temporada?.toLowerCase().includes(searchTermLower));
        break;
      default:
        filtered = products.filter(p => 
          p.code.toLowerCase().includes(searchTermLower) ||
          p.description.toLowerCase().includes(searchTermLower) ||
          p.tagNames.toLowerCase().includes(searchTermLower) ||
          p.temporada?.toLowerCase().includes(searchTermLower)
        );
    }

    setFilteredProducts(filtered);
  };

  const handleDisplayModeChange = (e) => {
    e.stopPropagation();
    setDisplayMode(prevMode => prevMode === "list" ? "catalog" : "list");
  };

  const selectedProduct = products.find(p => p.id === value);
  const activeFiltersCount = (filters.temporada ? 1 : 0) + (filters.tagIds?.length || 0);

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <label htmlFor="product-search" className="sr-only">Buscar producto</label>
          <Input
            id="product-search"
            name="product-search"
            type="text"
            placeholder="Buscar producto..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => setIsOpen(true)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            className="pr-10"
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
        <div>
          <label htmlFor="search-type" className="sr-only">Tipo de búsqueda</label>
          <select
            id="search-type"
            name="search-type"
            className="rounded-md border border-input bg-background px-3 py-2"
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
          >
            <option value="all">Todo</option>
            <option value="code">Código</option>
            <option value="description">Descripción</option>
            <option value="tags">Etiquetas</option>
            <option value="temporada">Temporada</option>
          </select>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleDisplayModeChange}
          className="flex items-center gap-2"
          aria-label={displayMode === "list" ? "Cambiar a vista catálogo" : "Cambiar a vista lista"}
        >
          {displayMode === "list" ? (
            <>
              <Grid className="h-4 w-4" /> Catálogo
            </>
          ) : (
            <>
              <List className="h-4 w-4" /> Lista
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setFiltersCollapsed(prev => !prev)}
          className="flex items-center gap-2"
          aria-expanded={!filtersCollapsed}
          aria-controls="product-filters"
        >
          {filtersCollapsed ? 'Mostrar filtros' : 'Ocultar filtros'}
          {activeFiltersCount > 0 && (
            <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </Button>
      </div>

      {/* Filtros de productos - independientes del foco de búsqueda */}
      {!filtersCollapsed && (
        <div id="product-filters" className="mt-3 p-3 bg-gray-50 rounded-md border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-blue-800">Filtros</span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setFilters(f => ({ ...f, temporada: null, tagIds: null }))}
              >
                Limpiar filtros
              </Button>
              <Button
                type="button"
                onClick={() => { 
                  try {
                    if (typeof onApplyFilters === 'function') {
                      const ids = (filteredProducts || []).map(p => p.id);
                      onApplyFilters({ productIds: ids, filters });
                    }
                  } catch (_) {}
                  setFiltersCollapsed(true); 
                  setIsOpen(false); 
                }}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                Aplicar y ocultar filtros
              </Button>
            </div>
          </div>
          <ProductFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            showActive={false} // No mostrar filtro de estado en el selector
            showTemporada={showSeasonFilter && !forcedTemporada}
            showClear={false}
          />
        </div>
      )}

      {/* Resumen compacto cuando los filtros están colapsados */}
      {isOpen && filtersCollapsed && activeFiltersCount > 0 && (
        <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 inline-flex gap-2">
          <span className="font-medium">Filtros activos:</span>
          {(showSeasonFilter || forcedTemporada) && (forcedTemporada || filters.temporada) && (
            <span className="px-2 py-0.5 bg-white border border-blue-200 rounded">📅 {forcedTemporada || filters.temporada}</span>
          )}
          {filters.tagIds?.length > 0 && (
            <span className="px-2 py-0.5 bg-white border border-blue-200 rounded">🏷️ {filters.tagIds.length}</span>
          )}
        </div>
      )}

      {isOpen && (
        <div className={`absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 overflow-auto ${
          displayMode === "list" ? "max-h-96" : "max-h-[800px]"
        }`}>
          {loading ? (
            <div className="p-4 text-center text-gray-500">Cargando...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No se encontraron productos</div>
          ) : displayMode === "list" ? (
            <div className="py-1">
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-start gap-3"
                  onClick={() => {
                    onSelect(product.id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                    <img
                      src={product.images?.[product.main_image_index] || '/placeholder.png'}
                      alt={product.description}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{product.code}</div>
                    <div className="text-sm text-gray-600 truncate">{product.description}</div>
                    {product.tagNames && (
                      <div className="text-xs text-gray-500 truncate">{product.tagNames}</div>
                    )}
                    {product.temporada && (
                      <div className="text-xs text-gray-500 truncate">Temporada: {product.temporada}</div>
                    )}
                    {product.composicion && (
                      <div className="text-xs text-gray-500 truncate">Composición: {product.composicion}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  className="flex flex-col items-center p-2 rounded-lg hover:bg-gray-100 focus:bg-gray-100 focus:outline-none min-h-[270px] border border-gray-200 shadow-sm bg-white"
                  onClick={() => {
                    onSelect(product.id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <div className="w-full aspect-square rounded-md overflow-hidden mb-2">
                    <img
                      src={product.images?.[product.main_image_index] || '/placeholder.png'}
                      alt={product.description}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="text-center w-full flex flex-col gap-0.5">
                    <div className="font-medium text-sm">{product.code}</div>
                    <div className="text-xs text-gray-600 line-clamp-2">{product.description}</div>
                    {product.tagNames && (
                      <div className="text-xs text-gray-500 truncate">{product.tagNames}</div>
                    )}
                    {product.temporada && (
                      <div className="text-xs text-gray-500 truncate">Temporada: {product.temporada}</div>
                    )}
                    {product.composicion && (
                      <div className="text-xs text-gray-500 break-words line-clamp-2 max-h-8 overflow-hidden">Composición: {product.composicion}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedProduct && (
        <div className="mt-2 p-2 bg-gray-50 rounded-md">
          <div className="flex justify-between items-start gap-3">
            <div className="flex gap-3">
              <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                <img
                  src={selectedProduct.images?.[selectedProduct.main_image_index] || '/placeholder.png'}
                  alt={selectedProduct.description}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div>
                <div className="font-medium">{selectedProduct.code}</div>
                <div className="text-sm text-gray-600">{selectedProduct.description}</div>
                {selectedProduct.tagNames && (
                  <div className="text-xs text-gray-500">{selectedProduct.tagNames}</div>
                )}
                {selectedProduct.temporada && (
                  <div className="text-xs text-gray-500">Temporada: {selectedProduct.temporada}</div>
                )}
                {selectedProduct.composicion && (
                  <div className="text-xs text-gray-500">Composición: {selectedProduct.composicion}</div>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onSelect(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
} 
